// External Libraries
import React, { useRef, useEffect } from 'react';
import { VariableSizeList } from 'react-window';
// CSS
import '../../styles/main.css';

// Components
import { Body, BodyColor, BodyType, Heading, TypographyColor, TypographyType } from '../../ui-elements';
import { GeoId } from '../../types/geo.types';

// TypeScript interfaces
interface Pincode {
  geoId: GeoId;
  geoName: string;
}

interface Parent {
  geoId: GeoId;
  geoName: string;
}

interface GeoData {
  geoId: GeoId;
  geoName: string;
  hierarchy: number[];
  presence_types: any[];
  parents: Parent[];
  pincode: Pincode[];
}

interface SearchResults {
  pageNumber: number;
  dataPerPage: number;
  totalRecords: number;
  totalPages: number;
  data: GeoData[];
}

// Table Row Component
interface TableRowProps {
  index: number;
  style: React.CSSProperties;
  // react-window will pass itemData as `data` prop — include both items and handler
  data: {
    items: GeoData[];
    handleListPress?: (item: GeoData) => void;
    // shared refs
    itemHeightsRef: React.MutableRefObject<number[]>;
    listRef: React.MutableRefObject<VariableSizeList | null>;
  };
}

const TableRow: React.FC<TableRowProps> = ({ index, style, data }) => {
  const { items, handleListPress, itemHeightsRef, listRef } = data;
  const item = items[index];
  const rowRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = rowRef.current;
    if (!el) return;
    const height = Math.max(el.getBoundingClientRect().height, 37); // ensure min height
    if (itemHeightsRef.current[index] !== height) {
      itemHeightsRef.current[index] = height;
      // notify list to recompute positions from this index
      listRef.current?.resetAfterIndex(index);
    }
  }, [index, itemHeightsRef, listRef, item]);

  const subDistrict = item.parents?.map(p => p.geoName).join(', ') || '--';
  const pincodes = item.pincode?.map(p => p.geoName).join(', ') || '--';

  return (
    <div style={style} className="mt-1">
      <div ref={rowRef} className="d-flex w-100 align-items-center p-2 cursor-pointer global-search-item" onClick={() => handleListPress?.(item)}>
        {/* Village Column */}
        <div className="d-flex align-items-center gap-2" style={{ width: '40%' }}>
          <Heading
            title={item.geoName}
            type={TypographyType.h5}
            colour={TypographyColor.primary}
          />
          {item.presence_types?.map((presenceData, pIndex) => (
            <div key={pIndex} className='d-flex flex-row align-items-center justify-content-center bg-light-grey gap-2 padding-left-right-2 padding-top-bottom-1 rounded'>
              <img
                src={presenceData?.icon}
                alt=""
                style={{ height: '1.5vh' }}
              />
              <Body
                type={BodyType.p3}
                color={BodyColor.primary}
                classname="p-0 m-0"
              >
                {presenceData?.properties?.count}
              </Body>
            </div>
          ))}
        </div>

        {/* Sub-District Column */}
        <div className="text-dark" style={{ width: '30%' }}>
          <Body
            type={BodyType.p3}
            color={BodyColor.primary}
            classname="p-0 m-0"
          >
            {subDistrict}
          </Body>
        </div>

        {/* Pincode Column */}
        <div className="text-dark d-flex flex-row justify-content-start align-items-center" style={{ width: '30%' }}>
          <Body
            type={BodyType.p3}
            color={BodyColor.primary}
            classname="p-0 m-0"
          >
            {pincodes}
          </Body>
        </div>
      </div>
    </div>
  );
};

// Main Component
interface GeoDataTableProps {
  results?: SearchResults | null;
  handleListPress?: (item: GeoData) => void;
}

export const GeoDataTable: React.FC<GeoDataTableProps> = ({ results = null, handleListPress }) => {
  const data = results?.data || [];
  const ROW_HEIGHT = 37;
  const TABLE_HEIGHT = 400;
  const listRef = useRef<VariableSizeList | null>(null);
  const itemHeightsRef = useRef<number[]>(new Array(data.length).fill(ROW_HEIGHT));

  // Ensure heights array length matches data length
  useEffect(() => {
    if (itemHeightsRef.current.length !== data.length) {
      itemHeightsRef.current = new Array(data.length).fill(ROW_HEIGHT);
    }
  }, [data.length]);

  if (!results || !results.data || results.data.length === 0) {
    return (
      <div className="w-100 text-center py-4">
        <Body type={BodyType.p3} color={BodyColor.secondary} classname="">
          No data found
        </Body>
      </div>
    );
  }

  return (
    <div className="w-100">
      {/* Table Header */}
      <div className="d-flex w-100 bg-light-grey-8 p-2 rounded" style={{ position: 'sticky', top: 0, zIndex: 10 }}>
        <div className="" style={{ width: '40%' }}>
          <Body
            type={BodyType.p4}
            color={BodyColor.secondary}
            classname="p-0 m-0"
          >
            VILLAGE
          </Body>
        </div>
        <div className="" style={{ width: '30%' }}>
          <Body
            type={BodyType.p4}
            color={BodyColor.secondary}
            classname="p-0 m-0"
          >
            SUB-DISTRICT
          </Body>
        </div>
        <div className="" style={{ width: '30%' }}>
          <Body
            type={BodyType.p4}
            color={BodyColor.secondary}
            classname="p-0 m-0"
          >
            PINCODE
          </Body>
        </div>
      </div>

      {/* Virtualized Table Body - variable row heights */}
      <VariableSizeList
        ref={(ref) => (listRef.current = ref)}
        height={TABLE_HEIGHT}
        itemCount={data.length}
        itemSize={(index) => itemHeightsRef.current[index] || ROW_HEIGHT}
        width="100%"
        itemData={{ items: data, handleListPress, itemHeightsRef, listRef }}
      >
        {TableRow}
      </VariableSizeList>
     </div>
   );
 };
